import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Table, Input, Alert, Spinner,
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  API_URL_users,
  API_URL_admin_set_password,
  API_URL_clients_import,
  API_URL_chatbot_admin_user_sede_access,
  API_URL_sedes,
} from "../constants";
import { getAuthHeaders, getCurrentUser } from "../utils/auth";

const ALLOWED_USER = "salvador@lidigo.io";

const UserAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importSede, setImportSede] = useState("");
  const [sedesList, setSedesList] = useState([]);

  const [accessTarget, setAccessTarget] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessIsRestricted, setAccessIsRestricted] = useState(false);
  const [accessAllSedes, setAccessAllSedes] = useState([]);
  const [accessAllowedCodes, setAccessAllowedCodes] = useState([]);
  const [accessSaving, setAccessSaving] = useState(false);

  const currentUser = getCurrentUser();
  const isAllowed = currentUser === ALLOWED_USER;

  useEffect(() => {
    if (!isAllowed) return;
    const headers = getAuthHeaders();
    axios
      .get(API_URL_users, { headers })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setUsers(data);
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setError("No se pudieron cargar los usuarios.");
      })
      .finally(() => setLoading(false));

    // Sedes for the import dropdown.
    axios
      .get(API_URL_sedes, { headers })
      .then((res) => setSedesList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSedesList([]));
  }, [isAllowed]);

  const filteredUsers = useMemo(() => {
    if (!filter) return users;
    const q = filter.toLowerCase();
    return users.filter((u) =>
      [u.username, u.email, String(u.id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, filter]);

  const openReset = (user) => {
    setResetTarget(user);
    setNewPassword("");
    setConfirmPassword("");
  };

  const closeReset = () => {
    if (submitting) return;
    setResetTarget(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleResetSubmit = async () => {
    if (!resetTarget) return;
    if (newPassword.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_URL_admin_set_password}${resetTarget.id}/`,
        { new_password: newPassword },
        { headers }
      );
      toast.success(`Contraseña actualizada para ${resetTarget.username}.`);
      setResetTarget(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("admin-set-password error:", err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || "No se pudo actualizar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClientImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset input so re-uploading same file works
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Selecciona un archivo .xlsx o .xls");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    if (importSede) formData.append("default_sede", importSede);

    try {
      const headers = getAuthHeaders();
      const res = await axios.post(API_URL_clients_import, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      toast.success(`Importación completa: ${res.data.created} nuevos, ${res.data.skipped_existing} ya existían.`);
    } catch (err) {
      console.error("client import:", err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || "No se pudo procesar el archivo.");
    } finally {
      setImporting(false);
    }
  };

  const openAccessModal = async (user) => {
    setAccessTarget(user);
    setAccessLoading(true);
    setAccessIsRestricted(false);
    setAccessAllSedes([]);
    setAccessAllowedCodes([]);
    try {
      const res = await axios.get(
        `${API_URL_chatbot_admin_user_sede_access}${user.id}/`,
        { headers: getAuthHeaders() }
      );
      setAccessIsRestricted(!!res.data.is_restricted);
      setAccessAllSedes(res.data.all_sedes || []);
      setAccessAllowedCodes(res.data.allowed_sede_codes || []);
    } catch (err) {
      toast.error("No se pudo cargar el acceso de Value Streams.");
      setAccessTarget(null);
    } finally {
      setAccessLoading(false);
    }
  };

  const closeAccessModal = () => {
    if (accessSaving) return;
    setAccessTarget(null);
  };

  const toggleSedeCode = (code) => {
    setAccessAllowedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const saveAccess = async () => {
    if (!accessTarget) return;
    setAccessSaving(true);
    try {
      await axios.put(
        `${API_URL_chatbot_admin_user_sede_access}${accessTarget.id}/`,
        {
          is_restricted: accessIsRestricted,
          allowed_sede_codes: accessAllowedCodes,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Permisos actualizados para ${accessTarget.username}.`);
      setAccessTarget(null);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo guardar el acceso.");
    } finally {
      setAccessSaving(false);
    }
  };

  if (!isAllowed) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Container style={{ marginTop: "20px" }}>
      <Row>
        <Col>
          <h1>Administración de Usuarios</h1>
          <p className="text-muted">
            Lista de usuarios registrados en el sistema.
          </p>
        </Col>
      </Row>

      {/* Bulk import card */}
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          backgroundColor: "#fafafa",
        }}
      >
        <h5 style={{ marginBottom: 6 }}>Importar clientes desde Excel</h5>
        <p className="text-muted small" style={{ marginBottom: 12 }}>
          Sube un export de Solución Factible (.xlsx). El sistema extrae los nombres únicos
          y crea sólo los clientes que no existen aún. Los campos adicionales (email, RFC, dirección)
          se pueden completar después en la pantalla de Clientes.
        </p>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label
            htmlFor="client-import-file"
            style={{
              display: "inline-block",
              padding: "8px 16px",
              backgroundColor: importing ? "#9ca3af" : "#dc3545",
              color: "#fff",
              borderRadius: 8,
              cursor: importing ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "0.875rem",
              marginBottom: 0,
            }}
          >
            {importing ? "Procesando..." : "Seleccionar archivo .xlsx"}
          </label>
          <input
            id="client-import-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleClientImport}
            disabled={importing}
            style={{ display: "none" }}
          />

          <span className="text-muted small" style={{ marginLeft: 12 }}>
            Value Stream a asignar:
          </span>
          <select
            value={importSede}
            onChange={(e) => setImportSede(e.target.value)}
            disabled={importing}
            style={{
              padding: "6px 10px",
              fontSize: "0.875rem",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              backgroundColor: "#fff",
              minWidth: 180,
            }}
          >
            <option value="">— (dejar en blanco) —</option>
            {sedesList.map((s) => (
              <option key={s.sede_code || s.sede} value={s.sede}>
                {s.sede}
              </option>
            ))}
          </select>

          {importing && <Spinner size="sm" color="primary" />}
        </div>

        {importResult && (
          <div style={{ marginTop: 12, padding: 10, backgroundColor: "#ecfdf5",
                        border: "1px solid #a7f3d0", borderRadius: 8, fontSize: "0.875rem" }}>
            <strong>{importResult.created}</strong> clientes nuevos creados,{" "}
            <strong>{importResult.skipped_existing}</strong> ya existían
            ({importResult.found} nombres únicos detectados en el archivo).
            {importResult.default_sede && (
              <span> Asignados al Value Stream <strong>{importResult.default_sede}</strong>.</span>
            )}
            {importResult.samples?.length > 0 && (
              <div style={{ marginTop: 6, color: "#047857" }}>
                Ejemplos nuevos: {importResult.samples.slice(0, 5).join(", ")}
                {importResult.samples.length > 5 ? "…" : ""}
              </div>
            )}
          </div>
        )}
      </div>

      <Row className="mb-3">
        <Col md={6}>
          <Input
            type="text"
            placeholder="Buscar por usuario, email o ID..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-md-end">
          <span className="text-muted">
            Total: {filteredUsers.length}
            {filter && users.length !== filteredUsers.length
              ? ` (de ${users.length})`
              : ""}
          </span>
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <Table striped responsive hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Activo</th>
              <th>Staff</th>
              <th>Último acceso</th>
              <th style={{ width: 1 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No hay usuarios para mostrar.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>{u.username}</td>
                  <td>{u.email || "—"}</td>
                  <td>{u.is_active ? "Sí" : "No"}</td>
                  <td>{u.is_staff ? "Sí" : "No"}</td>
                  <td>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Button
                      color="warning"
                      size="sm"
                      onClick={() => openReset(u)}
                      style={{ marginRight: 6 }}
                    >
                      Resetear contraseña
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      outline
                      onClick={() => openAccessModal(u)}
                    >
                      Permisos AI
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* AI access modal */}
      <Modal isOpen={!!accessTarget} toggle={closeAccessModal} size="md">
        <ModalHeader toggle={closeAccessModal}>
          Permisos AI — {accessTarget?.username || ""}
        </ModalHeader>
        <ModalBody>
          {accessLoading ? (
            <div className="text-center my-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : (
            <>
              <p className="text-muted small">
                Controla qué Value Streams puede consultar este usuario en el Asistente AI.
                Los filtros se aplican a nivel de herramienta — el modelo NO puede ver datos fuera del alcance.
              </p>

              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="restricted-switch"
                  checked={accessIsRestricted}
                  onChange={(e) => setAccessIsRestricted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="restricted-switch">
                  <strong>Restringir acceso</strong>
                </label>
                <div className="form-text">
                  Si está desactivado, el usuario ve todos los Value Streams (default).
                </div>
              </div>

              {accessIsRestricted && (
                <div>
                  <label className="form-label fw-semibold">Value Streams permitidos</label>
                  <div style={{
                    maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb",
                    borderRadius: 8, padding: 10,
                  }}>
                    {accessAllSedes.length === 0 ? (
                      <span className="text-muted">No hay Value Streams en el sistema.</span>
                    ) : (
                      accessAllSedes.map((s) => (
                        <div key={s.sede_code} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`sede-${s.sede_code}`}
                            checked={accessAllowedCodes.includes(s.sede_code)}
                            onChange={() => toggleSedeCode(s.sede_code)}
                          />
                          <label className="form-check-label" htmlFor={`sede-${s.sede_code}`}>
                            {s.sede}
                            <span className="text-muted small ms-2">({s.sede_code})</span>
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {accessIsRestricted && accessAllowedCodes.length === 0 && (
                    <div className="form-text text-warning">
                      ⚠️ Sin Value Streams seleccionados: el usuario no podrá consultar datos.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeAccessModal} disabled={accessSaving}>
            Cancelar
          </Button>
          <Button color="primary" onClick={saveAccess} disabled={accessSaving || accessLoading}>
            {accessSaving ? "Guardando..." : "Guardar permisos"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!resetTarget} toggle={closeReset}>
        <ModalHeader toggle={closeReset}>
          Resetear contraseña
          {resetTarget ? ` — ${resetTarget.username}` : ""}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Esta acción establece una nueva contraseña directamente. Comunícala
            de forma segura al usuario y pídele que la cambie cuanto antes.
          </p>
          <div className="mb-3">
            <label className="form-label">Nueva contraseña</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="form-label">Confirmar contraseña</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeReset} disabled={submitting}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleResetSubmit} disabled={submitting}>
            {submitting ? "Guardando..." : "Confirmar reset"}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default UserAdmin;
